import { useState } from 'react';
import Button from '../Button';
import { renameEnterpriseClass } from '../../api/admin';

/**
 * 单个活跃班级行：行内改名 / 转走成员 / 停用。
 * 注：AILeader 隐藏讲师列；编号列显示完整注册码 registration_code。
 *
 * Props:
 *   entId        企业 id
 *   cls          班级行 { id, name, registration_code, member_count }
 *   isLastActive 是否是企业唯一活跃班（true 时禁用「停用」）
 *   onTransfer   点「转走成员」回调（父级开 TransferMembersModal）
 *   onDisable    点「停用」回调（父级执行 disable + 刷新）
 *   onRenamed    改名成功回调（父级刷新本区）
 *   onError      错误回调（父级用 Alert 展示）
 */
export default function ClassRowEditor({ entId, cls, isLastActive, onTransfer, onDisable, onRenamed, onError }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(cls.name);
  const [saving, setSaving] = useState(false);

  const hasMembers = cls.member_count > 0;
  const canDisable = !hasMembers && !isLastActive;
  const disableReason = hasMembers ? '请先转走全部成员' : (isLastActive ? '不能停用最后一个活跃班级' : '');

  const startEdit = () => { setName(cls.name); setEditing(true); };

  const save = async () => {
    if (!name.trim()) { onError?.('班级名称不能为空'); return; }
    if (name.trim() === cls.name) { setEditing(false); return; }
    setSaving(true);
    try {
      await renameEnterpriseClass(entId, cls.id, { name: name.trim() });
      setEditing(false);
      onRenamed?.();
    } catch (e) {
      onError?.(e.response?.data?.detail || '改名失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={rowStyle}>
      <code style={codeStyle}>{cls.registration_code}</code>

      {editing ? (
        <input value={name} onChange={(e) => setName(e.target.value)} style={editInputStyle} autoFocus />
      ) : (
        <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{cls.name}</span>
      )}

      <span style={metaStyle}>成员 {cls.member_count}</span>

      <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
        {editing ? (
          <>
            <Button size="sm" onClick={save} loading={saving}>保存</Button>
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>取消</Button>
          </>
        ) : (
          <>
            <button onClick={startEdit} style={actionBtn('#2563eb')}>改名</button>
            <button onClick={() => onTransfer?.(cls)} style={actionBtn('#7c3aed')}>转走成员</button>
            <button
              onClick={() => canDisable && onDisable?.(cls)}
              disabled={!canDisable}
              title={disableReason}
              style={{ ...actionBtn('#d97706'), ...(canDisable ? {} : disabledBtn) }}
            >
              停用
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const rowStyle = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10 };
const codeStyle = { fontSize: 11, background: '#f1f5f9', padding: '2px 8px', borderRadius: 6, color: '#475569' };
const editInputStyle = { padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, flex: 1 };
const metaStyle = { fontSize: 12, color: '#94a3b8' };
const actionBtn = (color) => ({ padding: '5px 12px', borderRadius: 8, border: `1px solid ${color}30`, background: `${color}0f`, color, fontSize: 12, cursor: 'pointer', fontWeight: 500 });
const disabledBtn = { opacity: 0.45, cursor: 'not-allowed' };
